import classnames from 'classnames'
import * as React from 'react'

import { Button } from './button'
import { Icon, IconNameType, isValidIconName } from './icon'

enum Size {
  small = 'px-2 text-3xs',
  medium = 'px-2.5 py-2 text-sm',
}

export type AlertTheme = 'info' | 'success' | 'warning' | 'error' | 'warningAlerts'

interface ThemeConfig {
  className: string
  icon: IconNameType
  iconClass: string
  buttonClass?: string
}

const ALERT_THEME: Record<AlertTheme, ThemeConfig> = {
  info: {
    className:
      'border-violet-600 bg-violet-600 text-violet-100 dark:text-violet-200 dark:bg-violet-800 dark:border-violet-500',
    icon: 'idea',
    iconClass: 'text-violet-200 dark:text-violet-300',
    buttonClass: 'hover:bg-violet-700',
  },
  success: {
    className: 'border-green-500 bg-green-500 text-green-100 dark:bg-green-800 dark:border-green-500',
    icon: 'checkmarkOutlined',
    iconClass: 'text-green-200 dark:text-green-300',
    buttonClass: 'hover:bg-green-600 dark:hover:bg-green-700',
  },
  warning: {
    className:
      'border-yellow-200 bg-yellow-200 text-yellow-700 dark:text-yellow-100 dark:bg-yellow-800 dark:border-yellow-500',
    icon: 'warningOutlined',
    iconClass: 'text-yellow-500 dark:text-yellow-200',
    buttonClass: 'hover:bg-yellow-300 dark:hover:bg-yellow-700',
  },
  error: {
    className: 'border-red-500 bg-red-500 text-red-100 dark:bg-red-800 dark:border-red-500',
    icon: 'clearCircle',
    iconClass: 'text-red-200 dark:text-red-300',
    buttonClass: 'hover:bg-red-600 dark:hover:bg-red-700',
  },
  warningAlerts: {
    className:
      'border-yellow-200 bg-yellow-50 text-gray-900 dark:border-yellow-500 dark:bg-yellow-800 dark:text-yellow-100',
    icon: 'warning',
    iconClass: 'text-yellow-200',
  },
}

interface Props {
  children: React.ReactNode
  className?: string
  theme: AlertTheme
  size?: keyof typeof Size
  icon?: AlertTheme | IconNameType | null
  onClose?: () => void
}

export function Alert({ children, className, theme = 'info', size = 'medium', icon, onClose }: Props) {
  const currentTheme = ALERT_THEME[theme]
  const currentIcon = isValidIconName(icon) ? icon : icon === null ? null : currentTheme.icon

  return (
    <div
      className={classnames(className, Size[size], currentTheme.className, 'flex items-start space-x-2.5 border', {
        'rounded-md': theme !== 'warningAlerts',
        rounded: theme === 'warningAlerts',
      })}
    >
      {currentIcon && (
        <Icon name={currentIcon} className={classnames(currentTheme.iconClass, 'mt-0.5 flex-none')} width={20} />
      )}
      <div className="mt-0.5 grow text-left">{children}</div>
      {onClose && (
        <Button
          type="button"
          size="small"
          theme="transparent"
          appearance="square"
          rounding="full"
          className={currentTheme.buttonClass}
          leftContent={<Icon name="close" width={20} />}
          onClick={onClose}
        />
      )}
    </div>
  )
}
